import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/core/components/ui/sheet';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Switch } from '@/core/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/core/components/ui/collapsible';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { Loader2, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { TemplateFieldWithHelper } from '../molecules/TemplateFieldWithHelper';
import {
  ManufacturerItem,
  CreateManufacturerRequest,
  UpdateManufacturerRequest,
  PriceTextPlaceholder,
  ManufacturerStatus,
  ManufacturerStatusLabels,
} from '../../types/manufacturer-types';

interface ManufacturerFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateManufacturerRequest | UpdateManufacturerRequest) => Promise<void>;
  manufacturer?: ManufacturerItem | null;
  isSubmitting: boolean;
  availablePlaceholders: PriceTextPlaceholder[];
  exampleTemplates: string[];
  finalCostExampleTemplates: string[];
  error?: string | null;
}

interface FormErrors {
  manufacturerName?: string;
  modelName?: string;
  priceTextTemplate?: string;
  priceTextOfflineTemplate?: string;
  finalCostPriceTextTemplate?: string;
}

export function ManufacturerForm({
  isOpen,
  onClose,
  onSubmit,
  manufacturer,
  isSubmitting,
  availablePlaceholders,
  exampleTemplates,
  finalCostExampleTemplates,
  error,
}: ManufacturerFormProps) {
  const isEdit = !!manufacturer;

  // Form state
  const [manufacturerName, setManufacturerName] = useState('');
  const [modelName, setModelName] = useState('');
  const [status, setStatus] = useState<number>(ManufacturerStatus.Active);
  const [isDc, setIsDc] = useState(false);
  const [isOcppTariff, setIsOcppTariff] = useState(false);
  const [isOcppCost, setIsOcppCost] = useState(false);
  const [priceTextTemplate, setPriceTextTemplate] = useState('');
  const [priceTextOfflineTemplate, setPriceTextOfflineTemplate] = useState('');
  const [finalCostPriceTextTemplate, setFinalCostPriceTextTemplate] = useState('');

  // UI state
  const [templatesExpanded, setTemplatesExpanded] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Initialize form when manufacturer changes
  useEffect(() => {
    if (manufacturer) {
      setManufacturerName(manufacturer.manufacturerName);
      setModelName(manufacturer.modelName || '');
      setStatus(manufacturer.status);
      setIsDc(manufacturer.isDc);
      setIsOcppTariff(manufacturer.isOcppTariff);
      setIsOcppCost(manufacturer.isOcppCost);
      setPriceTextTemplate(manufacturer.priceTextTemplate || '');
      setPriceTextOfflineTemplate(manufacturer.priceTextOfflineTemplate || '');
      setFinalCostPriceTextTemplate(manufacturer.finalCostPriceTextTemplate || '');
      // Expand templates section if any template has content
      if (
        manufacturer.priceTextTemplate ||
        manufacturer.priceTextOfflineTemplate ||
        manufacturer.finalCostPriceTextTemplate
      ) {
        setTemplatesExpanded(true);
      }
    } else {
      resetForm();
    }
  }, [manufacturer, isOpen]);

  const resetForm = () => {
    setManufacturerName('');
    setModelName('');
    setStatus(ManufacturerStatus.Active);
    setIsDc(false);
    setIsOcppTariff(false);
    setIsOcppCost(false);
    setPriceTextTemplate('');
    setPriceTextOfflineTemplate('');
    setFinalCostPriceTextTemplate('');
    setTemplatesExpanded(false);
    setErrors({});
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!manufacturerName.trim()) {
      newErrors.manufacturerName = 'Manufacturer name is required';
    } else if (manufacturerName.length > 100) {
      newErrors.manufacturerName = 'Maximum 100 characters';
    }

    if (modelName.length > 100) {
      newErrors.modelName = 'Maximum 100 characters';
    }

    if (priceTextTemplate.length > 500) {
      newErrors.priceTextTemplate = 'Maximum 500 characters';
    }

    if (priceTextOfflineTemplate.length > 500) {
      newErrors.priceTextOfflineTemplate = 'Maximum 500 characters';
    }

    if (finalCostPriceTextTemplate.length > 500) {
      newErrors.finalCostPriceTextTemplate = 'Maximum 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const data: CreateManufacturerRequest | UpdateManufacturerRequest = {
      ...(isEdit && { id: manufacturer!.id }),
      manufacturerName: manufacturerName.trim(),
      modelName: modelName.trim() || null,
      status,
      isDc,
      isOcppTariff,
      isOcppCost,
      priceTextTemplate: priceTextTemplate.trim() || null,
      priceTextOfflineTemplate: priceTextOfflineTemplate.trim() || null,
      finalCostPriceTextTemplate: finalCostPriceTextTemplate.trim() || null,
    };

    await onSubmit(data);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent className="w-full sm:max-w-xl flex flex-col p-0">
        <div className="px-6 pt-6">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit Manufacturer' : 'Add New Manufacturer'}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'Update the manufacturer details below.'
                : 'Fill in the details to create a new manufacturer.'}
            </SheetDescription>
          </SheetHeader>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="manufacturerName">
                  Manufacturer Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="manufacturerName"
                  value={manufacturerName}
                  onChange={(e) => setManufacturerName(e.target.value)}
                  placeholder="Enter manufacturer name"
                  className={errors.manufacturerName ? 'border-red-500' : ''}
                  maxLength={100}
                />
                {errors.manufacturerName && (
                  <p className="text-sm text-red-600 mt-1">{errors.manufacturerName}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">{manufacturerName.length}/100</p>
              </div>

              <div>
                <Label htmlFor="modelName">Model Name</Label>
                <Input
                  id="modelName"
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                  placeholder="Enter model name (optional)"
                  className={errors.modelName ? 'border-red-500' : ''}
                  maxLength={100}
                />
                {errors.modelName && (
                  <p className="text-sm text-red-600 mt-1">{errors.modelName}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">{modelName.length}/100</p>
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={status.toString()} onValueChange={(v) => setStatus(Number(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ManufacturerStatusLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Feature Flags */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Features</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="isDc" className="cursor-pointer">
                    DC Charger Support
                  </Label>
                  <Switch id="isDc" checked={isDc} onCheckedChange={setIsDc} />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="isOcppTariff" className="cursor-pointer">
                    OCPP Tariff Support
                  </Label>
                  <Switch id="isOcppTariff" checked={isOcppTariff} onCheckedChange={setIsOcppTariff} />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="isOcppCost" className="cursor-pointer">
                    OCPP Cost Support
                  </Label>
                  <Switch id="isOcppCost" checked={isOcppCost} onCheckedChange={setIsOcppCost} />
                </div>
              </div>
            </div>

            {/* Price Text Templates - Collapsible */}
            <Collapsible open={templatesExpanded} onOpenChange={setTemplatesExpanded}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between h-auto py-3"
                >
                  <div className="flex flex-col items-start text-left">
                    <span className="font-medium">Price Display Templates</span>
                    <span className="text-xs font-normal text-gray-500">
                      Configure how pricing appears on charger screens
                    </span>
                  </div>
                  {templatesExpanded ? (
                    <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 ml-2 flex-shrink-0" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-4">
                {/* Running Cost Template */}
                <TemplateFieldWithHelper
                  label="Running Cost Display"
                  description="Shown during active charging session"
                  value={priceTextTemplate}
                  onChange={setPriceTextTemplate}
                  placeholders={availablePlaceholders}
                  exampleTemplates={exampleTemplates}
                  placeholder="e.g., {pv}{c}/kWh"
                  error={errors.priceTextTemplate}
                />

                {/* Offline Template */}
                <TemplateFieldWithHelper
                  label="Offline Display"
                  description="Shown when charger cannot fetch live pricing"
                  value={priceTextOfflineTemplate}
                  onChange={setPriceTextOfflineTemplate}
                  placeholders={availablePlaceholders}
                  exampleTemplates={exampleTemplates}
                  placeholder="e.g., {pv}{c}/kWh"
                  error={errors.priceTextOfflineTemplate}
                />

                {/* Final Cost Template */}
                <TemplateFieldWithHelper
                  label="Final Cost Display"
                  description="Shown after charging session ends"
                  value={finalCostPriceTextTemplate}
                  onChange={setFinalCostPriceTextTemplate}
                  placeholders={availablePlaceholders}
                  exampleTemplates={finalCostExampleTemplates.length > 0 ? finalCostExampleTemplates : exampleTemplates}
                  placeholder="e.g., Total: {pv}{c}"
                  error={errors.finalCostPriceTextTemplate}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>

          {/* Sticky footer */}
          <SheetFooter className="sticky bottom-0 border-t bg-white px-6 py-4 mt-auto">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : isEdit ? (
                'Update Manufacturer'
              ) : (
                'Create Manufacturer'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
